import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Container,
    Button,
    Paper,
    Grid,
    Select,
    MenuItem,
    FormControl,
    CircularProgress
} from '@mui/material';
import axios from 'axios';
import { DateTime } from 'luxon';
import { timezoneList } from './timezoneList';

const days = ['Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'];
const dayMap = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thurs: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday'
};

const brandGreen = '#12c099';

const dayMapReverse = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thurs', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const mapTimeToLabel = (start) => {
    const hour = parseInt(start.split(':')[0], 10);
    if (hour >= 6 && hour <= 9) return 'Morning';
    if (hour >= 10 && hour <= 12) return 'Midday';
    if (hour >= 13 && hour <= 16) return 'Afternoon';
    if (hour >= 17 && hour <= 20) return 'Evening';
    if (hour >= 21) return 'Night';
    return 'Unknown';
};

const preSelected = {
    'Tue-Morning': true,
    'Wed-Midday': true,
    'Thurs-Afternoon': true
};

const use24HourFormat = (locale) => {
    const dtf = new Intl.DateTimeFormat(locale, { hour: 'numeric' });
    return !dtf.formatToParts(new Date()).some(part => part.type === 'dayPeriod');
};

const getTimeRanges = (timezone, locale) => {
    const format = use24HourFormat(locale) ? 'HH:mm' : 'h:mm a';

    return {
        Morning: {
            start: '06:00',
            end: '09:00',
            label: `${DateTime.fromObject({ hour: 6 }, { zone: timezone }).toFormat(format)} - ${DateTime.fromObject({ hour: 9 }, { zone: timezone }).toFormat(format)}`
        },
        Midday: {
            start: '10:00',
            end: '12:00',
            label: `${DateTime.fromObject({ hour: 10 }, { zone: timezone }).toFormat(format)} - ${DateTime.fromObject({ hour: 12 }, { zone: timezone }).toFormat(format)}`
        },
        Afternoon: {
            start: '13:00',
            end: '16:00',
            label: `${DateTime.fromObject({ hour: 13 }, { zone: timezone }).toFormat(format)} - ${DateTime.fromObject({ hour: 16 }, { zone: timezone }).toFormat(format)}`
        },
        Evening: {
            start: '17:00',
            end: '20:00',
            label: `${DateTime.fromObject({ hour: 17 }, { zone: timezone }).toFormat(format)} - ${DateTime.fromObject({ hour: 20 }, { zone: timezone }).toFormat(format)}`
        },
        Night: {
            start: '21:00',
            end: '23:00',
            label: `${DateTime.fromObject({ hour: 21 }, { zone: timezone }).toFormat(format)} - ${DateTime.fromObject({ hour: 23 }, { zone: timezone }).toFormat(format)}`
        }
    };
};

const normalizePhone = (raw) => {
    if (!raw) return '';
    // Remove all non-digit characters
    const digits = raw.replace(/\D/g, '');
    // Handle US numbers, which is the primary case
    if (digits.length === 10) {
        return `+1${digits}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
    }
    // If the raw string already has a +, trust it
    if (raw.startsWith('+')) {
        return raw;
    }
    // Fallback for any other case
    return `+${digits}`;
};

const PreferencesPage = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userPreferences, setUserPreferences] = useState(null); // null: loading, {}: new user, {data}: existing
    const [selected, setSelected] = useState({}); // Grid state, will be populated by fetch
    const [firstName, setFirstName] = useState('');
    const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [locale, setLocale] = useState(navigator.language);
    const [timeRanges, setTimeRanges] = useState(getTimeRanges(timezone, locale));
    const { phone: rawPhone } = useParams(); // Rename to avoid confusion

    useEffect(() => {
        const fetchUserData = async () => {
            // NORMALIZE THE PHONE NUMBER FIRST
            const phone = normalizePhone(rawPhone);

            if (!phone) {
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                // 1. Attempt to get full preference details USING THE NORMALIZED PHONE
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/preferences/${encodeURIComponent(phone)}`
                );
                const prefs = res.data;
                setUserPreferences(prefs);
                setFirstName(prefs.name ? prefs.name.split(' ')[0] : 'there');

                // 2. Populate the grid with the user's saved selections
                const savedSelections = {};
                (prefs.preferredDays || []).forEach(dayFullName => {
                    const dayAbbrev = dayMapReverse[dayFullName];
                    (prefs.timeRanges || []).forEach(time => {
                        const label = mapTimeToLabel(time.start);
                        if (dayAbbrev && label !== 'Unknown') {
                            savedSelections[`${dayAbbrev}-${label}`] = true;
                        }
                    });
                });
                setSelected(savedSelections);

            } catch (error) {
                if (error.response && error.response.status === 404) {
                    // 3. This is a NEW USER. Set defaults.
                    setUserPreferences({}); // Mark as new user
                    setSelected(preSelected); // Show the example selections

                    // Fetch name as a fallback
                    try {
                        const nameRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/preferences/profile/${encodeURIComponent(phone)}`);                        setFirstName(nameRes.data.firstName || 'there');
                    } catch (nameErr) {
                        console.error('Failed to fetch name for new user:', nameErr);
                        setFirstName('there');
                    }
                } else {
                    console.error('Error fetching preferences:', error);
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchUserData();
    }, [rawPhone]);



    useEffect(() => {
        setTimeRanges(getTimeRanges(timezone, locale));
    }, [timezone, locale]);

    const times = Object.keys(timeRanges);

    const handleTimezoneChange = (e) => {
        const newTz = e.target.value;
        setTimezone(newTz);
    };

    const toggleCell = (day, time) => {
        const key = `${day}-${time}`;
        setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const selectAllForDay = (day) => {
        const updated = { ...selected };
        const allSelected = times.every((time) => selected[`${day}-${time}`]);

        times.forEach((time) => {
            updated[`${day}-${time}`] = !allSelected;
        });

        setSelected(updated);
    };


    const selectAllForTime = (time) => {
        const updated = { ...selected };
        const allSelected = days.every((day) => selected[`${day}-${time}`]);

        days.forEach((day) => {
            updated[`${day}-${time}`] = !allSelected;
        });

        setSelected(updated);
    };


    const clearAll = () => setSelected({});

// THE CORRECTED CODE (USING YOUR ORIGINAL ALERT LOGIC)
    const handleSubmit = async () => {
        const phone = normalizePhone(rawPhone); // This line is correct and necessary
        setIsSubmitting(true);

        // This transformation logic is correct and can be reused
        const selectedEntries = Object.entries(selected).filter(([_, v]) => v).map(([key]) => {
            const [day, timeLabel] = key.split('-');
            return { day, timeLabel };
        });
        const preferredDays = [...new Set(selectedEntries.map(entry => dayMap[entry.day]))];
        const uniqueTimeRanges = new Set(selectedEntries.map(entry => JSON.stringify({
            start: timeRanges[entry.timeLabel].start, end: timeRanges[entry.timeLabel].end
        })));
        const timeRangesPayload = [...uniqueTimeRanges].map(str => JSON.parse(str));

        try {
            // Get the response back from the server
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/preferences/${phone}`, {
                preferredDays,
                timeRanges: timeRangesPayload,
                notes: ''
            });

            // ✅ THIS IS YOUR ORIGINAL LOGIC, NOW RESTORED
            setUserPreferences(res.data.preference);
            alert('Preferences saved successfully!');

        } catch (err) {
            console.error(err);
            alert('Error saving preferences');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ADD THESE LINES right before the `return (`
    const hasSubmittedBefore = userPreferences && userPreferences.submittedAt;
    const lastUpdatedDate = hasSubmittedBefore ? new Date(userPreferences.submittedAt).toLocaleDateString() : '';



    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#000', py: 12 }}>
            <Container maxWidth="md">
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                    {hasSubmittedBefore ? 'Update Your Availability' : 'Set Your Preferences'}
                </Typography>
                <Typography sx={{ color: '#aaa', mb: 1, fontSize: '1.1rem' }}>
                    {hasSubmittedBefore
                        ? `Hi ${firstName}, your preferences were last updated on ${lastUpdatedDate}. You can change them below.`
                        : `Hi ${firstName}, tap any box to mark your best times to train. We’ll use this to suggest local sessions.`
                    }
                </Typography>

                {/* --- CONDITIONAL "EXAMPLES" TEXT --- */}
                {!hasSubmittedBefore && (
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', mb: 3 }}>
                        (Pre-selected examples shown below — you can change them.)
                    </Typography>
                )}

                <Box sx={{ mb: 3 }}>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', mb: 1 }}>
                        Times shown in your local timezone:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <Select
                            value={timezone}
                            onChange={handleTimezoneChange}
                            sx={{
                                backgroundColor: '#1a1a1a',
                                color: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #444',
                                fontSize: '0.85rem',
                                '.MuiSelect-icon': {
                                    color: brandGreen
                                }
                            }}
                        >
                            {timezoneList.map((tz) => (
                                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Grid container spacing={1} sx={{ mb: 3 }}>
                    {days.map((day) => (
                        <Grid item key={day}>
                            <Button onClick={() => selectAllForDay(day)} size="small" variant="outlined" sx={{ color: brandGreen, borderColor: brandGreen, fontSize: '0.75rem' }}>
                                {day} All Times
                            </Button>
                        </Grid>
                    ))}
                    {times.map((time) => (
                        <Grid item key={time}>
                            <Button onClick={() => selectAllForTime(time)} size="small" variant="outlined" sx={{ color: brandGreen, borderColor: brandGreen, fontSize: '0.75rem' }}>
                                {time} All Days
                            </Button>
                        </Grid>
                    ))}
                    <Grid item>
                        <Button onClick={clearAll} size="small" variant="outlined" sx={{ color: '#ff5e5e', borderColor: '#ff5e5e', fontSize: '0.75rem' }}>
                            Clear All
                        </Button>
                    </Grid>
                </Grid>

                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: `100px repeat(${days.length}, 1fr)`, gap: 1 }}>
                        <Box></Box>
                        {days.map((day) => (
                            <Typography key={day} sx={{ color: '#fff', textAlign: 'center', fontWeight: 600 }}>{day}</Typography>
                        ))}
                        {times.map((time) => (
                            <React.Fragment key={time}>
                                <Box>
                                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>{time}</Typography>
                                    <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>{timeRanges[time].label}</Typography>
                                </Box>
                                {days.map((day) => {
                                    const key = `${day}-${time}`;
                                    const isSelected = selected[key];
                                    return (
                                        <Box
                                            key={key}
                                            onClick={() => toggleCell(day, time)}
                                            sx={{
                                                height: 48,
                                                borderRadius: '8px',
                                                backgroundColor: isSelected ? brandGreen : '#2c2c2c',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: isSelected ? brandGreen : '#444'
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

                <Button
                    fullWidth
                    variant="contained"
                    disabled={isSubmitting} // Add this
                    sx={{
                        mt: 6,
                        backgroundColor: brandGreen,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        py: 1.5,
                        '&:hover': { backgroundColor: '#15d9a5' }
                    }}
                    onClick={handleSubmit}
                >
                    {isSubmitting
                        ? <CircularProgress size={24} sx={{ color: '#000' }} />
                        : (hasSubmittedBefore ? 'Update Availability' : 'Submit Preferences')}
                </Button>
            </Container>
        </Box>
    );
};

export default PreferencesPage;
